import { Component, EventEmitter, Inject, Output, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { DialogGeneral } from 'app/dialogs/DialogGeneral';
import { formatKennitala } from 'app/helpers/formatKennitala';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { ListDocumentRes } from 'app/interfaces/responses/List/listDocumentRes';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { SkjalamidjaService } from 'app/services/api/skjalamidja/skjalamidja.service';
import { USER_ROLES } from 'common/constants/user-role.constants';

export interface DialogDocumentsOverviewFileData {
    document: ListDocumentRes;
}

@Component({
    selector: 'dialog-documents-overview-file',
    templateUrl: 'dialog-documents-overview-file.html',
    styleUrls: ['dialog-documents-overview-file.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class DialogDocumentsOverviewFile extends DialogGeneral {
    @Output() getDocument = new EventEmitter<ListDocumentRes>();
    @Output() fetchData = new EventEmitter<null>();

    userDetails: ExposedReadUserDetailsRes = null;
    canPerformActions: boolean = false;

    constructor(
        private formBuilder: UntypedFormBuilder,
        public matDialogRef: MatDialogRef<DialogDocumentsOverviewFile>,
        public dialogRef: MatDialogRef<DialogDocumentsOverviewFile>,
        public dialog: MatDialog,
        private skjalamidja: SkjalamidjaService,
        private _fuseConfirmationService: FuseConfirmationService,
        public lsrauthService: LsrauthService,
        @Inject(MAT_DIALOG_DATA) public data: DialogDocumentsOverviewFileData
    ) {
        super(matDialogRef);
        this.lsrauthService.userDetails.subscribe((userDetails) => (this.userDetails = userDetails));
    }

    ngOnInit(): void {
        this.canPerformActions = this.checkCanPerformActions();
    }

    handleGetDocument(document) {
        this.getDocument.emit(document);
        this.close();
    }

    withdrawDocument(removeFromPortalLSR) {
        let configForm = this.formBuilder.group({
            title: 'Eyða skjali ' + (removeFromPortalLSR ? 'af Mínum síðum LSR' : 'af Ísland.is'),
            message: removeFromPortalLSR
                ? 'Skjali verður eytt af Mínum síðum LSR. Ef skjal er þegar skráð á Ísland.is verður því einnig eytt þar.'
                : 'Skjali verður eytt af vefsvæði Íslands.is',
            icon: {
                show: true,
                name: 'heroicons_outline:document-remove',
                color: 'primary',
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'Staðfesta',
                    color: 'primary',
                },
                cancel: {
                    show: true,
                    label: 'Hætta við',
                },
            },
            dismissible: true,
        });

        const dialogRef = this._fuseConfirmationService.open(configForm.value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result == 'confirmed') {
                this.close();
                this.skjalamidja
                    .withdrawDocument({
                        DocumentId: this.data.document.documentId,
                        Reason: null,
                        RemoveFromPortalLSR: removeFromPortalLSR,
                    })
                    .subscribe((x) => {
                        this.fetchData.emit();
                    });
            }
        });
    }

    removeDocument() {
        let configForm = this.formBuilder.group({
            title: 'Eyða skjali af Ísland.is, Mínum síðum LSR og Snjallari LSR',
            message: 'Skjali verður eytt af Mínum síðum LSR og Snjallari LSR. Ef skjal er þegar skráð á Ísland.is verður því einnig eytt þar.' +
                '<br><strong>Athugið að skjalið er ekki aðgengilegt starfsmanni eftir að skjalinu hefur verið eytt.</strong>',
            icon: {
                show: true,
                name: 'heroicons_outline:document-remove',
                color: 'primary',
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'Staðfesta',
                    color: 'primary',
                },
                cancel: {
                    show: true,
                    label: 'Hætta við',
                },
            },
            dismissible: true,
        });

        const dialogRef = this._fuseConfirmationService.open(configForm.value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result == 'confirmed') {
                this.close();
                this.skjalamidja.deleteDocument({
                    DocumentId: this.data.document.documentId,
                    Reason: null
                })
                    .subscribe((x) => {
                        this.fetchData.emit();
                    });
            }
        });
    }

    submitToDigitalIceland() {
        let configForm = this.formBuilder.group({
            title: 'Senda skjal á Ísland.is',
            message: 'Skjal verður sent til birtingar á vefsvæði Íslands.is',
            icon: {
                show: true,
                name: 'heroicons_outline:check-circle',
                color: 'primary',
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'Staðfesta',
                    color: 'primary',
                },
                cancel: {
                    show: true,
                    label: 'Hætta við',
                },
            },
            dismissible: true,
        });

        const dialogRef = this._fuseConfirmationService.open(configForm.value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result == 'confirmed') {
                this.close();
                this.skjalamidja.submitToDigitalIceland(this.data.document.documentId).subscribe((x) => {
                    this.fetchData.emit();
                });
            }
        });
    }

    submitToPortalLSR() {
        let configForm = this.formBuilder.group({
            title: 'Senda skjal á Mínar síður LSR',
            message: 'Skjal verður sent til birtingar á Mínum síðum LSR',
            icon: {
                show: true,
                name: 'heroicons_outline:check-circle',
                color: 'primary',
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'Staðfesta',
                    color: 'primary',
                },
                cancel: {
                    show: true,
                    label: 'Hætta við',
                },
            },
            dismissible: true,
        });

        const dialogRef = this._fuseConfirmationService.open(configForm.value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result == 'confirmed') {
                this.close();
                this.skjalamidja.submitToPortalLSR(this.data.document.documentId).subscribe((x) => {
                    this.fetchData.emit();
                });
            }
        });
    }

    formatKennitala(kt) {
        return formatKennitala(kt);
    }

    // Users can perform actions if they are superusers or if they are the owner of the document or if they have access to the division the document is in
    checkCanPerformActions(): boolean {
        return (
            this.lsrauthService.inRole(this.userDetails, USER_ROLES.DOCUMENT_CENTER_SUPERUSER) ||
            (this.lsrauthService.inRole(this.userDetails, USER_ROLES.UPLOAD_DOCUMENTS) &&
                (this.data.document.userIdEdit === this.userDetails.user.userId ||
                    (this.lsrauthService.inRole(this.userDetails, USER_ROLES.DOCUMENT_ACCESS_FILES_WITH_DIVISION) &&
                        this.userDetails.documentDivisions.some((x) => x.divisionName === this.data.document.svid && x.canReadDocuments))))
        );
    }
}
